// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

// --- Services ---
import rolesSvc from "../../services/api/roles";

// --- Models ---
import { RoleItem, RoleFilter } from "../../models/types/role";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";

// --- Utils ---
import dayjs from "dayjs";

// --- Icons ---
import { Trash2, Pencil, Monitor } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function RolesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation();

    const [data, setData] = useState<RoleItem[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<RoleItem | null>(null);

    const getListAll = async (params: DefaultFilterRequest<RoleFilter>) => {
        setLoading(true);
        try {
            const res = await rolesSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify(t("common.error"), t("common.errorOccurred"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: RoleItem) => {
        try {
            const res = await rolesSvc.deleteRole(row.id);
            if (res && res.content.status) {
                notify(t("roles.success"), t("roles.deleteSuccess"), "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify(t("common.error"), t("common.errorOccurred"), "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: DefaultFilterRequest<RoleFilter> = {
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            name: queryParams.get("name") || undefined,
            isDelete: queryParams.get("isDelete") || "false",
        };

        await getListAll(params);
    };
    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: RoleItem) => {
        navigate(UrlCollection.EditRole.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: RoleItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const columns = [
        { header: t("roles.table.stt"), accessor: "index", sortable: false, className: "max-w-[50px]" },
        { header: t("roles.table.name"), accessor: "name", sortable: true },
        { header: t("roles.table.code"), accessor: "code", sortable: true },
        {
            header: t("roles.table.createdDate"),
            accessor: (row: RoleItem) => `${dayjs(row.created_date).format("DD/MM/YYYY")}`,
            sortable: true,
        },
        {
            header: t("roles.table.modifiedDate"),
            accessor: (row: RoleItem) => `${dayjs(row.modified_date).format("DD/MM/YYYY")}`,
            sortable: true,
        },
        {
            header: t("roles.table.actions"),
            accessor: (row: RoleItem) => (
                <div className="flex gap-2">

                    <ButtonAction
                        disabled={row.code === "ADMIN"}
                        onClick={() => handleEdit(row)}
                        icon={<Pencil size={14} />}
                        color="blue"
                        title={t("roles.actions.edit")}
                    />
                    <ButtonAction
                        disabled={row.code === "ADMIN"}
                        onClick={() => { notify(t("common.notification"), t("common.updating"), "warning") }}
                        icon={<Monitor size={14} />}
                        color="gray"
                        title={t("roles.actions.permissions")}
                    />
                    <ButtonAction
                        disabled={row.code === "ADMIN"}
                        onClick={() => handleDelete(row)}
                        icon={<Trash2 size={14} />}
                        color="red"
                        title={t("roles.actions.delete")}
                    />
                </div>
            ),
            className: "max-w-[100px]",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header bg-gray-100">
                <h1 className="text-xl font-semibold">{t("roles.title")}</h1>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateRole)}
                >
                    {t("roles.addNew")}
                </button>
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title="Xác nhận"
                    content="Bạn có chắc chắn muốn xóa tham số này?"
                    titleComfirm="Xác nhận"
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
        </div>
    );
}
